import React, { useEffect, useState, useLayoutEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  Chip,
  Paper,
  CircularProgress,
  Grid,
  Avatar,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import LocationOnIcon from "@mui/icons-material/LocationOn";
// ArrowBackIcon удалён
import { apiUrl, fileUrl } from "../config/api";
import { getCurrentLang } from "../components/LangSelector";
import useSEO from "../hooks/useSEO";

const DetailedJobDescription = () => {
  const [jobDetails, setJobDetails] = useState(null);
  const [learnText, setLearnText] = useState("");
  const [loadingLearn, setLoadingLearn] = useState(false);
  const [error, setError] = useState("");
  const [applyOpen, setApplyOpen] = useState(false);
  const [applyEmail, setApplyEmail] = useState("");
  const [applyFile, setApplyFile] = useState(null);
  const [applyLoading, setApplyLoading] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [snackOpen, setSnackOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const fileRef = useRef(null);

  const navigate = useNavigate();
  const { id, lang: urlLang } = useParams();
  const [isEntering, setIsEntering] = useState(true);

  // Если в URL есть lang (например /ru/job/123), синхронизируем с localStorage
  useEffect(() => {
    if (urlLang && ['ro', 'ru', 'en'].includes(urlLang)) {
      if (getCurrentLang() !== urlLang) {
        localStorage.setItem('app_lang', urlLang);
        window.dispatchEvent(new CustomEvent('app:langChange', { detail: urlLang }));
      }
    }
  }, [urlLang]);

  const seoLang = urlLang || getCurrentLang();
  const seoUrl = seoLang === 'ro' ? `/job/${id}` : `/${seoLang}/job/${id}`;

  // SEO — заголовок обновится когда загрузятся данные
  useSEO({
    title: jobDetails
      ? `${jobDetails.title}${jobDetails.company ? ` - ${jobDetails.company}` : ''} | JobMD`
      : 'Вакансия | JobMD',
    description: jobDetails
      ? (jobDetails.description || '').replace(/<[^>]*>/g, '').substring(0, 200)
      : 'Подробное описание вакансии в Молдове',
    url: seoUrl,
  });

  // JSON-LD JobPosting Schema
  useEffect(() => {
    if (!jobDetails) return;

    const cleanDesc = (jobDetails.description || '').replace(/<[^>]*>/g, '').trim();
    const salaryStr = jobDetails.salary_from || jobDetails.salary_to
      ? `${jobDetails.salary_from ? 'от ' + jobDetails.salary_from : ''}${jobDetails.salary_from && jobDetails.salary_to ? ' — ' : ''}${jobDetails.salary_to ? 'до ' + jobDetails.salary_to : ''} ${jobDetails.salary_currency || 'USD'}`
      : undefined;

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'JobPosting',
      title: jobDetails.title,
      description: cleanDesc.substring(0, 4000),
      datePosted: jobDetails.date_added || jobDetails.created_at,
      validThrough: jobDetails.valid_through || undefined,
      hiringOrganization: {
        '@type': 'Organization',
        name: jobDetails.company || 'JobMD',
        sameAs: jobDetails.website || undefined,
      },
      jobLocation: {
        '@type': 'Place',
        address: {
          '@type': 'PostalAddress',
          addressLocality: jobDetails.city || jobDetails.location_city || 'Кишинев',
          addressCountry: 'MD',
        },
      },
      employmentType: jobDetails.work_schedule === 'Полный день' ? 'FULL_TIME'
        : jobDetails.work_schedule === 'Частичная занятость' ? 'PART_TIME'
        : jobDetails.work_schedule === 'Удаленная работа' ? 'REMOTE'
        : jobDetails.work_schedule === 'Проектная работа' ? 'CONTRACTOR'
        : undefined,
      experienceRequirements: jobDetails.work_experience || undefined,
      educationRequirements: jobDetails.education || undefined,
      ...(salaryStr ? {
        baseSalary: {
          '@type': 'MonetaryAmount',
          currency: jobDetails.salary_currency || 'USD',
          value: {
            '@type': salaryStr.includes('до') ? 'QuantitativeValue' : 'QuantitativeValue',
            ...(jobDetails.salary_from ? { minValue: jobDetails.salary_from } : {}),
            ...(jobDetails.salary_to ? { maxValue: jobDetails.salary_to } : {}),
            ...(!jobDetails.salary_from && !jobDetails.salary_to ? { value: jobDetails.salary } : {}),
          },
        },
      } : {}),
      applicantLocationRequirements: {
        '@type': 'Country',
        name: 'MD',
      },
      ...(jobDetails.image_url ? { image: jobDetails.image_url.startsWith('http') ? jobDetails.image_url : `https://jobmd.top${jobDetails.image_url}` } : {}),
      url: `https://jobmd.top${seoUrl}`,
    };

    // Удаляем старый скрипт, добавляем новый
    const oldScript = document.getElementById('jobposting-schema');
    if (oldScript) oldScript.remove();

    const script = document.createElement('script');
    script.id = 'jobposting-schema';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema, null, 2);
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById('jobposting-schema');
      if (s) s.remove();
    };
  }, [jobDetails, id]);

  useLayoutEffect(() => {
    // Убираем класс перехода при входе (контенту)
    const content = document.querySelector(".transition-content");
    if (content) {
      content.classList.remove("page-transition-content");
      content.classList.add("page-enter-content");
      
      const timer = setTimeout(() => {
        content.classList.remove("page-enter-content");
        setIsEntering(false);
      }, 400);
      
      return () => clearTimeout(timer);
    }
    setIsEntering(false);
  }, []);

  const loadJob = useCallback(() => {
    const lang = urlLang || getCurrentLang();
    window.scrollTo({ top: 0, behavior: "instant" });

    fetch(`${apiUrl("jobs.php")}?id=${id}&lang=${lang}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.job) {
          setJobDetails(data.job);
        } else if (data.success && data.jobs?.length > 0) {
          // Fallback для старого формата
          setJobDetails(data.jobs[0]);
        } else {
          setError(data.message || "Вакансия не найдена");
        }
      })
      .catch((err) => {
        setError("Ошибка загрузки: " + err.message);
      });
  }, [id, urlLang]);

  useEffect(() => {
    loadJob();
    // Перезагружаем при смене языка
    window.addEventListener('app:langChange', loadJob);
    return () => window.removeEventListener('app:langChange', loadJob);
  }, [loadJob]);

  // Сохранить в историю просмотров при загрузке
  useEffect(() => {
    if (!jobDetails) return;
    try {
      const raw = localStorage.getItem('viewedJobs') || '[]';
      const history = JSON.parse(raw);
      const filtered = history.filter(j => j.id !== jobDetails.id);
      const cleanDesc = jobDetails.description ? jobDetails.description.replace(/<[^>]*>/g, '').trim() : '';
      filtered.unshift({
        id: jobDetails.id,
        title: jobDetails.title,
        company: jobDetails.company,
        description: cleanDesc.substring(0, 200),
        city: jobDetails.city || jobDetails.location_city || '',
        salary: jobDetails.salary || '',
        image_url: jobDetails.image_url || jobDetails.logo_url || '',
        date_added: jobDetails.created_at || jobDetails.date_added || '',
      });
      localStorage.setItem('viewedJobs', JSON.stringify(filtered.slice(0, 50)));
    } catch (e) {
      console.error('Ошибка сохранения истории:', e);
    }
  }, [jobDetails]);

  // ---------- 🤖 AI: Автоматический анализ при загрузке вакансии ----------
  useEffect(() => {
    if (!jobDetails) return;

    // Сначала проверяем localStorage (на случай если уже загружали раньше)
    const cacheKey = `ai_advice_${id}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        setAiResult(parsed);
        return; // уже есть, не дёргаем API
      } catch (e) {}
    }

    // Запускаем SSE
    setAiLoading(true);
    setAiResult(null);

    const eventSource = new EventSource(
      `https://jobmd.top/api/ai_job_advice.php?job_id=${id}&type=analysis`
    );

    let fullAdvice = '';

    eventSource.addEventListener('cached', (e) => {
      // Пришёл полный кешированный результат
      try {
        const data = JSON.parse(e.data);
        if (data.advice) {
          fullAdvice = data.advice;
          const result = {
            advice: fullAdvice,
            job_id: id,
            type: 'analysis',
            _cached: true,
          };
          setAiResult(result);
          localStorage.setItem(cacheKey, JSON.stringify(result));
        }
      } catch (err) {}
    });

    eventSource.addEventListener('start', () => {
      // Стрим начался — показываем пустой блок
      setAiResult({ advice: '', _streaming: true });
    });

    eventSource.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        
        if (data.chunk) {
          fullAdvice += data.chunk;
          setAiResult(prev => {
            if (!prev || prev._cached) {
              return { advice: fullAdvice, _streaming: true };
            }
            return { ...prev, advice: fullAdvice, _streaming: true };
          });
        }
      } catch (err) {}
    };

    eventSource.addEventListener('error', (e) => {
      // Ошибка стрима
      if (fullAdvice) {
        // Сохраняем то что успели настримить
        const result = { advice: fullAdvice, _partial: true };
        setAiResult(result);
        localStorage.setItem(cacheKey, JSON.stringify(result));
      } else {
        setAiResult({ error: 'Не удалось загрузить анализ' });
      }
      setAiLoading(false);
      eventSource.close();
    });

    eventSource.addEventListener('done', () => {
      // Стрим завершён — сохраняем полный текст
      const result = {
        advice: fullAdvice,
        job_id: id,
        type: 'analysis',
        _streaming: false,
        _cached: true,
      };
      setAiResult(result);
      localStorage.setItem(cacheKey, JSON.stringify(result));
      setAiLoading(false);
      eventSource.close();
    });

    return () => {
      eventSource.close();
    };
  }, [jobDetails, id]);

  if (!jobDetails && !error) {
    return (
      <Box textAlign="center" mt={10}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box maxWidth="1200px" mx="auto" mt={10} p={3}>
        <Typography variant="h5" color="error" gutterBottom>
          Ошибка загрузки
        </Typography>
        <Typography>{error}</Typography>
        <Button variant="contained" onClick={() => navigate("/")} sx={{ mt: 2 }}>
          На главную
        </Button>
      </Box>
    );
  }

  const cleanText =
    jobDetails.description?.replace(/<\/?[^>]+(>|$)/g, "") || "";
  
  // Адаптация полей для совместимости со старыми и новыми вакансиями
  const adaptedJob = {
    ...jobDetails,
    // Для новых вакансий
    image_url: jobDetails.image_url ? (jobDetails.image_url.startsWith('http') ? jobDetails.image_url : fileUrl(jobDetails.image_url)) : null,
    city: jobDetails.city || jobDetails.location_city || '',
    salary: jobDetails.salary || 
      (jobDetails.salary_from && jobDetails.salary_to 
        ? `${jobDetails.salary_from} - ${jobDetails.salary_to} ${jobDetails.salary_currency || 'USD'}`
        : jobDetails.salary_from 
          ? `от ${jobDetails.salary_from} ${jobDetails.salary_currency || 'USD'}`
          : jobDetails.salary_to 
            ? `до ${jobDetails.salary_to} ${jobDetails.salary_currency || 'USD'}`
            : 'Зарплата не указана'),
    phone: jobDetails.phone || jobDetails.contact_phone || '',
    email: jobDetails.email || jobDetails.contact_email || '',
    company: jobDetails.company || jobDetails.company_name || '',
    work_experience: jobDetails.work_experience || jobDetails.experience_level || '',
    work_schedule: jobDetails.work_schedule || jobDetails.employment_type || '',
    education: jobDetails.education || jobDetails.education_level || ''
  };

  return (
    <Box 
      maxWidth="1200px" 
      mx="auto" 
      mt={3}
      className={isEntering ? "card-expand" : ""}
      sx={{
        opacity: isEntering ? 0 : 1,
        transition: "opacity 0.3s ease",
      }}
    >

      <Box sx={{
        width: "100%",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        flexWrap: "wrap",
        gap: "24px",
        boxSizing: "border-box"
      }}>

        {/* 🔥 LEFT */}
        <Box sx={{
          flex: { xs: "0 0 100%", md: "0 0 67%" },
          width: { xs: "100%", md: "67%" },
          maxWidth: { xs: "100%", md: "67%" },
          boxSizing: "border-box"
        }}>

          {/* HEADER */}
          <Paper sx={{ p: 3, borderRadius: 2, mb: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">

              <Avatar
                src={adaptedJob.image_url}
                sx={{ width: 60, height: 60 }}
              />

              <Box>
                <Typography variant="h5" fontWeight="bold">
                  {adaptedJob.title}
                </Typography>

                <Stack direction="row" spacing={1} mt={1}>

                  <Chip
                    icon={<LocationOnIcon />}
                    label={adaptedJob.city}
                    size="small"
                    sx={{
                      background: "rgba(99,102,241,0.1)",
                      color: "#6366f1",
                    }}
                  />

                  <Chip
                    label={adaptedJob.salary}
                    size="small"
                    sx={{
                      background: "rgba(34,197,94,0.1)",
                      color: "#16a34a",
                    }}
                  />

                </Stack>

                <Typography variant="body2" color="text.secondary" mt={2}>
                  <Box
                    component="span"
                    onClick={() => navigate(`/company/${encodeURIComponent(adaptedJob.company)}`)}
                    sx={{
                      cursor: "pointer",
                      color: "primary.main",
                      fontWeight: 500,
                      "&:hover": { textDecoration: "underline", opacity: 0.8 },
                    }}
                  >
                    {adaptedJob.company}
                  </Box>
                  {" • "}{adaptedJob.work_experience} • {adaptedJob.work_schedule}
                </Typography>

              </Box>
            </Stack>
          </Paper>

          {/* DESCRIPTION */}
          <Paper sx={{ p: 3, borderRadius: 2, mb: 2 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Описание вакансии
            </Typography>

            <Divider sx={{ mb: 2 }} />

            <Typography variant="body1" sx={{ whiteSpace: "pre-line" }}>
              {cleanText}
            </Typography>
          </Paper>

          {/* REQUIREMENTS */}
          {adaptedJob.requirements && (
            <Paper sx={{ p: 3, borderRadius: 2, mb: 2 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Требования
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body1" sx={{ whiteSpace: "pre-line" }}>
                {adaptedJob.requirements}
              </Typography>
            </Paper>
          )}

          {/* 🤖 AI Анализ — что нужно знать */}
          {(aiLoading || aiResult) && (
            <Paper sx={{
              p: 3,
              borderRadius: 2,
              mb: 2,
              background: aiLoading && !aiResult?.advice
                ? "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)"
                : "linear-gradient(135deg, #faf5ff 0%, #f0e6ff 100%)",
              border: "1px solid",
              borderColor: aiResult?._streaming ? "rgba(139, 92, 246, 0.4)" : "rgba(139, 92, 246, 0.2)",
              position: "relative",
              overflow: "hidden",
              transition: "all 0.3s ease",
              '&::before': aiResult?._streaming ? {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 3,
                background: 'linear-gradient(90deg, #8b5cf6, #6366f1, #8b5cf6)',
                backgroundSize: '200% 100%',
                animation: 'shimmer 1.5s ease-in-out infinite',
              } : {},
            }}>
              {/* Декоративная иконка сбоку */}
              <Box sx={{
                position: 'absolute',
                right: -20,
                top: -20,
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: aiResult?._streaming
                  ? 'radial-gradient(circle, rgba(139,92,246,0.08) 0%, transparent 70%)'
                  : 'radial-gradient(circle, rgba(139,92,246,0.05) 0%, transparent 70%)',
                pointerEvents: 'none',
              }} />

              <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                <Box sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1.5,
                  background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 18,
                  boxShadow: '0 4px 12px rgba(99,102,241,0.3)',
                }}>🤖</Box>
                <Typography fontWeight="bold" sx={{ color: '#4c1d95', fontSize: '1.05rem' }}>
                  Анализ вакансии
                </Typography>
                {aiLoading && !aiResult?.advice && (
                  <CircularProgress size={14} sx={{ color: '#8b5cf6', ml: 'auto' }} />
                )}
                {aiResult?._cached && !aiResult?._streaming && (
                  <Chip
                    label="Из кеша"
                    size="small"
                    sx={{
                      ml: 'auto',
                      height: 20,
                      fontSize: 10,
                      background: 'rgba(34,197,94,0.1)',
                      color: '#16a34a',
                      fontWeight: 600,
                    }}
                  />
                )}
                {aiResult?._streaming && (
                  <Chip
                    label="Генерация..."
                    size="small"
                    sx={{
                      ml: 'auto',
                      height: 20,
                      fontSize: 10,
                      background: 'rgba(139,92,246,0.1)',
                      color: '#7c3aed',
                      fontWeight: 600,
                      animation: 'pulse 1.5s ease-in-out infinite',
                    }}
                  />
                )}
              </Stack>

              <Divider sx={{
                mb: 2,
                borderColor: aiResult?._streaming ? 'rgba(139,92,246,0.2)' : 'rgba(139,92,246,0.1)',
              }} />

              <Box sx={{
                position: 'relative',
              }}>
                {!aiResult?.advice && aiLoading ? (
                  <Box>
                    {[1,2,3].map(i => (
                      <Box key={i} sx={{
                        height: 12,
                        borderRadius: 1,
                        mb: 1.5,
                        width: i === 2 ? '85%' : i === 3 ? '60%' : '92%',
                        background: 'linear-gradient(90deg, #e0d4ff 0%, #f0e6ff 50%, #e0d4ff 100%)',
                        backgroundSize: '200% 100%',
                        animation: 'shimmer 1.5s ease-in-out infinite',
                      }} />
                    ))}
                    <Box sx={{
                      height: 12,
                      borderRadius: 1,
                      width: '40%',
                      background: 'linear-gradient(90deg, #e0d4ff 0%, #f0e6ff 50%, #e0d4ff 100%)',
                      backgroundSize: '200% 100%',
                      animation: 'shimmer 1.5s ease-in-out infinite',
                      animationDelay: '0.2s',
                    }} />
                  </Box>
                ) : (
                  <Typography
                    variant="body1"
                    sx={{
                      whiteSpace: "pre-line",
                      lineHeight: 1.9,
                      color: '#1e1b4b',
                      fontSize: '0.95rem',
                      position: 'relative',
                      zIndex: 1,
                      '& strong': { color: '#4c1d95' },
                    }}
                  >
                    {aiResult?.advice || ''}
                    {aiResult?._streaming && (
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-block',
                          width: 8,
                          height: 18,
                          background: '#8b5cf6',
                          ml: 0.5,
                          borderRadius: '2px',
                          animation: 'blink 0.8s step-end infinite',
                          verticalAlign: 'middle',
                        }}
                      />
                    )}
                  </Typography>
                )}
              </Box>

              {aiResult?.advice && !aiResult?._streaming && (
                <Typography variant="caption" sx={{
                  display: 'block',
                  mt: 2,
                  color: '#8b5cf6',
                  opacity: 0.6,
                  textAlign: 'right',
                  fontWeight: 500,
                  letterSpacing: '0.3px',
                }}>
                  ✦ Проанализировано AI
                </Typography>
              )}
            </Paper>
          )}

          {aiResult?.error && (
            <Box sx={{ p: 2, borderRadius: 1, background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)", mb: 2 }}>
              <Typography variant="body2" color="error">❌ {aiResult.error}</Typography>
            </Box>
          )}

        </Box>

        {/* 🔥 RIGHT - Боковая панель с откликом и информацией */}
        <Box sx={{
          flex: { xs: "0 0 100%", md: "0 0 30%" },
          width: { xs: "100%", md: "30%" },
          maxWidth: { xs: "100%", md: "30%" },
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          gap: "20px"
        }}>

          {/* КАРТОЧКА ОТКЛИКА */}
          <Paper sx={{
            p: 3,
            borderRadius: 2,
            background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
            border: "1px solid rgba(99, 102, 241, 0.1)",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)"
          }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom color="#6366f1">
              🚀 Откликнуться на вакансию
            </Typography>
            
            <Button
              variant="contained"
              fullWidth
              size="large"
              sx={{
                py: 2,
                borderRadius: 1.5,
                fontWeight: 700,
                fontSize: "1.1rem",
                background: "linear-gradient(135deg, #059669 0%, #10b981 100%)",
                boxShadow: "0 6px 20px rgba(5, 150, 105, 0.3)",
                "&:hover": {
                  background: "linear-gradient(135deg, #047857 0%, #059669 100%)",
                  boxShadow: "0 8px 25px rgba(5, 150, 105, 0.4)",
                  transform: "translateY(-2px)"
                },
                transition: "all 0.3s ease"
              }}
              onClick={() => setApplyOpen(true)}
            >
              📨 Отправить CV
            </Button>



            <Box sx={{ mt: 3, p: 2, background: "rgba(5, 150, 105, 0.06)", borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                ⚡ Быстрый отклик за 2 минуты
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Прикрепите резюме
                • Напишите сопроводительное письмо
                • Получите ответ от работодателя
              </Typography>
            </Box>
          </Paper>

          {/* МОДАЛКА ОТКЛИКА */}
          <Dialog open={applyOpen} onClose={() => { if(!applyLoading) setApplyOpen(false) }} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 700 }}>📨 Отправить CV</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {adaptedJob.title} — {adaptedJob.company}
              </Typography>
              <TextField
                label="Ваш Email"
                type="text"
                fullWidth
                value={applyEmail}
                onChange={(e) => setApplyEmail(e.target.value)}
                sx={{ mb: 2 }}
                disabled={applyLoading}
              />
              <input
                type="file"
                accept=".pdf"
                ref={fileRef}
                style={{ display: 'none' }}
                onChange={(e) => setApplyFile(e.target.files[0])}
              />
              <Button
                variant="outlined"
                fullWidth
                onClick={() => fileRef.current?.click()}
                disabled={applyLoading}
                sx={{ py: 1.5, mb: applyFile ? 1 : 0 }}
              >
                {applyFile ? '✅ ' + applyFile.name : '📎 Загрузить PDF резюме'}
              </Button>
              {applyResult && (
                <Alert severity={applyResult.success ? 'success' : 'error'} sx={{ mt: 2 }}>
                  {applyResult.message}
                  {applyResult.name && <div><strong>{applyResult.name}</strong> {applyResult.phone && '• ' + applyResult.phone} {applyResult.city && '• ' + applyResult.city}</div>}
                  {applyResult.skills && <div style={{ fontSize: '0.85em', color: '#666', marginTop: 4 }}>Навыки: {applyResult.skills}</div>}
                </Alert>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 2, pt: 0 }}>
              <Button onClick={() => setApplyOpen(false)} disabled={applyLoading}>Отмена</Button>
              <Button
                variant="contained"
                disabled={!applyEmail || !applyFile || applyLoading}
                color="success"
                onClick={async () => {
                  // Валидация
                  if (!applyEmail || !applyFile) return;
                  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                  if (!emailRegex.test(applyEmail)) {
                    setApplyResult({ success: false, message: 'Неверный формат email' });
                    setApplyLoading(false);
                    return;
                  }
                  if (applyFile.type !== 'application/pdf') {
                    setApplyResult({ success: false, message: 'Можно загружать только PDF' });
                    setApplyLoading(false);
                    return;
                  }
                  if (applyFile.size > 5 * 1024 * 1024) {
                    setApplyResult({ success: false, message: 'Файл слишком большой. Максимум 5MB' });
                    setApplyLoading(false);
                    return;
                  }
                  setApplyLoading(true);
                  setApplyResult(null);

                  // UUID
                  let uuid = localStorage.getItem('apply_uuid');
                  if (!uuid) {
                    uuid = 'apply_' + Date.now() + '_' + Math.random().toString(36).substring(2, 10);
                    localStorage.setItem('apply_uuid', uuid);
                  }

                  const reader = new FileReader();
                  reader.onload = async (e) => {
                    const base64 = e.target.result.split(',')[1];
                    try {
                      const res = await fetch('https://jobmd.top/api/apply.php', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          email: applyEmail,
                          job_id: id,
                          uuid,
                          file_base64: base64,
                          file_name: applyFile.name
                        }),
                      });
                      let data;
                      try {
                        data = await res.json();
                      } catch (e) {
                        data = { success: false, message: 'Ошибка сервера (статус ' + res.status + ')' };
                      }
                      // Нормализуем: если в ответе есть error но нет message — используем error как message
                      if (!data.message && data.error) {
                        data.message = data.error;
                      }
                      setApplyResult(data);
                      setSnackOpen(true);
                    } catch (err) {
                      setApplyResult({ success: false, message: 'Ошибка сети: ' + err.message });
                    }
                    setApplyLoading(false);
                    // Блокировка кнопки на 12 секунд
                    setTimeout(() => setApplyLoading(false), 12000);
                  };
                  reader.readAsDataURL(applyFile);
                }}
              >
                {applyLoading ? <CircularProgress size={24} /> : '🚀 Отправить CV'}
              </Button>
            </DialogActions>
          </Dialog>

          <Snackbar open={snackOpen} autoHideDuration={5000} onClose={() => { setSnackOpen(false); setApplyOpen(false); }}>
            <Alert severity={applyResult?.success ? 'success' : 'error'} variant="filled">
              {applyResult?.success ? '✅ ' + (applyResult.message || 'Отклик отправлен!') : '❌ ' + (applyResult?.message || applyResult?.error || 'Ошибка')}
            </Alert>
          </Snackbar>

          {/* КАРТОЧКА ИНФОРМАЦИИ О ВАКАНСИИ */}
          <Paper sx={{
            p: 3,
            borderRadius: 2,
            background: "#ffffff",
            border: "1px solid rgba(0, 0, 0, 0.08)"
          }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              📋 Информация о вакансии
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Stack spacing={2.5}>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                  🏢 Компания
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight="medium"
                  onClick={() => navigate(`/company/${encodeURIComponent(adaptedJob.company)}`)}
                  sx={{
                    color: "#1e293b",
                    cursor: "pointer",
                    "&:hover": { color: "primary.main", textDecoration: "underline" },
                  }}
                >
                  {adaptedJob.company}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                  💰 Зарплата
                </Typography>
                <Typography variant="body1" fontWeight="medium" sx={{ color: "#16a34a" }}>
                  {adaptedJob.salary}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                  📍 Локация
                </Typography>
                <Typography variant="body1" fontWeight="medium" sx={{ color: "#1e293b" }}>
                  {adaptedJob.city}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                  ⏳ Опыт работы
                </Typography>
                <Typography variant="body1" fontWeight="medium" sx={{ color: "#1e293b" }}>
                  {adaptedJob.work_experience}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                  🕐 График работы
                </Typography>
                <Typography variant="body1" fontWeight="medium" sx={{ color: "#1e293b" }}>
                  {adaptedJob.work_schedule}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                  🎓 Образование
                </Typography>
                <Typography variant="body1" fontWeight="medium" sx={{ color: "#1e293b" }}>
                  {adaptedJob.education}
                </Typography>
              </Box>
            </Stack>
          </Paper>

          {/* КАРТОЧКА КОНТАКТОВ */}
          {(adaptedJob.phone || adaptedJob.email) && (
            <Paper sx={{
              p: 3,
              borderRadius: 2,
              background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
              border: "1px solid rgba(245, 158, 11, 0.2)"
            }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom color="#d97706">
                📞 Контакты работодателя
              </Typography>
              <Divider sx={{ mb: 3, borderColor: "rgba(245, 158, 11, 0.3)" }} />

              <Stack spacing={2}>
                {adaptedJob.phone && (
                  <Box sx={{
                    p: 2,
                    background: "rgba(255, 255, 255, 0.7)",
                    borderRadius: 1,
                    border: "1px solid rgba(245, 158, 11, 0.1)"
                  }}>
                    <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                      📱 Телефон
                    </Typography>
                    <Typography variant="body1" fontWeight="medium" sx={{ color: "#1e293b" }}>
                      {adaptedJob.phone}
                    </Typography>
                  </Box>
                )}

                {adaptedJob.email && (
                  <Box sx={{
                    p: 2,
                    background: "rgba(255, 255, 255, 0.7)",
                    borderRadius: 1,
                    border: "1px solid rgba(245, 158, 11, 0.1)"
                  }}>
                    <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                      ✉️ Email
                    </Typography>
                    <Typography variant="body1" fontWeight="medium" sx={{ color: "#1e293b" }}>
                      {adaptedJob.email}
                    </Typography>
                  </Box>
                )}
              </Stack>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: "italic" }}>
                💡 Контакты доступны только зарегистрированным пользователям
              </Typography>
            </Paper>
          )}

        </Box>

      </Box>

    </Box>
  );
};

export default DetailedJobDescription;